import ActionsPopover from "@/components/ActionsPopOver/ActionsPopOver";
import ActionsPopOverItem from "@/components/ActionsPopOver/ActionsPopOverItem";
import FakeLink from "@/components/FakeLink/FakeLink";
import ImpactPill from "@/components/Impact/ImpactPill";
import MoreButton from "@/components/MoreButton/MoreButton";
import PageButtonGroup from "@/components/PageButtonGroup/PageButtonGroup";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import StatusTag from "@/components/StatusTag/StatusTag";
import StatusTags from "@/components/StatusTags/StatusTags";
import UnrelatedTag from "@/components/StatusTags/UnrelatedTag";
import SwitchWithLabel from "@/components/SwitchWithLabel/SwitchWithLabel";
import Table, { Column } from "@/components/Table/Table";
import useTableStateUrl from "@/components/Table/hooks/useTableStateUrl";
import TableHeader from "@/components/TableHeader/TableHeader";
import YearSelector from "@/components/YearSelector/YearSelector";
import useAnalytics from "@/hooks/useAnalytics";
import useTrackOnLoad from "@/hooks/useTrackPageview";
import useUrlStateBoolean from "@/hooks/useUrlStateBoolean";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import { IImpact } from "@/pages/dashboard/utils/types";
import {
  ApiOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, DropdownProps, Space, Tooltip } from "antd";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import DeletePackagingMaterialAction from "../DeletePackagingMaterialAction";

import {
  useGetPackagingMaterials,
  usePackagingMaterialStatusPerYear,
} from "../usePackagingMaterials";

import styles from "./PackagingMaterialsOverview.module.scss";
import MaterialImpactModal from "@/pages/packaging_materials/PackagingMaterialImpactModal/MaterialImpactReferenceModal";
import DeleteMaterialImpactReferenceAction from "@/pages/packaging_materials/PackagingMaterialImpactModal/DeletePackagingMaterialImpactReference";

export interface PackagingMaterial {
  id: number;
  name: string;
  supplier_name: string | null;
  supplier_address: string;
  status: "complete" | "incomplete" | "out_of_scope";
  match?: {
    id: number;
    name: string;
    impact?: IImpact;
  };
  packaging_relations: number;
  facility_relations: number;
  packaging_unrelated: boolean;
}

export default function PackagingMaterialsOverview() {
  useTrackOnLoad("Packaging Materials Viewed");

  const [moreDropdownOpen, setMoreDropdownOpen] = React.useState(false);
  const [selectedMaterials, setSelectedMaterials] = useState<PackagingMaterial[]>([]);
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const { year } = useDashboardYear();
  const [showUnrelated, setShowUnrelated] = useUrlStateBoolean("showUnrelated", false);

  const deselectUndeletableMaterials = () => {
    setSelectedMaterials((selectedMaterials) => [
      ...selectedMaterials.filter((x) => x.packaging_relations === 0 && x.facility_relations === 0),
    ]);
  };

  const columns: Column<PackagingMaterial>[] = [
    {
      key: "name",
      label: "Material",
      sortable: true,
      searchable: true,
      minWidth: "160px",
    },
    {
      key: "supplier_name",
      label: "Supplier",
      sortable: true,
      searchable: true,
      minWidth: "160px",
    },
    {
      key: "supplier_address",
      label: "Supplier address",
      sortable: true,
      searchable: true,
      minWidth: "220px",
    },
    {
      key: "dataset_name",
      label: "Impact match",
      sortable: true,
      searchable: true,
      minWidth: "190px",
      renderer: (record) => (record.match ? record.match.name : "-"),
    },
    {
      key: "impact_match",
      label: "Impact",
      maxWidth: "250px",
      minWidth: "250px",
      renderer: (record) => (
        <ImpactPill
          impact={record.match?.impact}
          incomplete={!record.match}
          incompleteTooltip={"Material does not have an impact reference assigned."}
        />
      ),
    },
    {
      key: "packaging_relations",
      label: "Packaging Relations",
      maxWidth: "200px",
      minWidth: "200px",
      sortable: true,
      renderer: (record) => (
        <>
          <span>{record.packaging_relations} packaging</span>
          {record.packaging_unrelated && (
            <Tooltip title="This material is not related to purchase or sales orders via a product, so it will be excluded from impact calculation">
              <>
                <UnrelatedTag />
              </>
            </Tooltip>
          )}
        </>
      ),
    },
    {
      key: "facility_relation",
      label: "Facility Relations",
      maxWidth: "180px",
      minWidth: "180px",
      sortable: true,
      renderer: (record) => (
        <>
          <span>{record.facility_relations} facilities</span>
        </>
      ),
    },
    {
      key: "status",
      label: "Status",
      maxWidth: "150px",
      minWidth: "150px",
      sortable: true,
      renderer: (product) => (
        <StatusTag
          status={product.status}
          showIcon={false}
        />
      ),
      filterable: true,
    },
    {
      key: "actions",
      label: "Actions",
      minWidth: "80px",
      maxWidth: "80px",
      noLink: true,
      renderer: (material) => (
        <ActionsPopover
          actions={
            <>
              <ActionsPopOverItem
                onClick={() => {
                  navigate(`${material.id}`);
                }}
                icon={<EditOutlined />}
              >
                Material details
              </ActionsPopOverItem>
              <MaterialImpactModal materials={[material]}>
                <ActionsPopOverItem icon={<ApiOutlined />}>
                  Edit impact reference
                </ActionsPopOverItem>
              </MaterialImpactModal>
              <DeleteMaterialImpactReferenceAction materialIds={[material.id]}>
                <ActionsPopOverItem
                  icon={<DeleteOutlined />}
                  danger={true}
                >
                  Delete impact reference
                </ActionsPopOverItem>
              </DeleteMaterialImpactReferenceAction>
              <DeletePackagingMaterialAction
                deselectUndeletableProducts={deselectUndeletableMaterials}
                onSucces={() => {
                  setSelectedMaterials([]);
                  analytics.track("Packaging Material Deleted");
                }}
                materials={[material]}
              >
                <ActionsPopOverItem
                  icon={<DeleteOutlined />}
                  disabled={material.packaging_relations > 0}
                  danger
                >
                  Delete material
                </ActionsPopOverItem>
              </DeletePackagingMaterialAction>
            </>
          }
        >
          <MoreOutlined />
        </ActionsPopover>
      ),
      align: "end",
    },
  ];

  const { pagination, filter, search, sort, onSearchFilterSortPaginate, setPage } =
    useTableStateUrl({
      columns,
      defaultSort: { key: "relations", order: "descend" },
    });
  const { data, isLoading, isFetching } = useGetPackagingMaterials({
    year,
    showUnrelated,
    filter,
    sort,
    search,
    pagination,
  });

  const handleOpenChange: DropdownProps["onOpenChange"] = (nextOpen, info) => {
    if (info.source === "trigger" || nextOpen) {
      setMoreDropdownOpen(nextOpen);
    }
  };

  const onSelectedKeysChange = (keys: number[]) => {
    analytics.track("Packaging Material Selected");

    setSelectedMaterials((currentMaterials) => {
      let productsOnOtherPages = [
        ...currentMaterials.filter((x) => !data?.data.map((x) => x.id).includes(x.id)),
      ];
      return [
        ...productsOnOtherPages,
        ...(data?.data.filter((material) => keys.includes(material.id)) || []),
      ];
    });
  };

  const resetTable = () => {
    setPage(1);
    setSelectedMaterials([]);
  };

  const { data: materialsYears } = usePackagingMaterialStatusPerYear();

  return (
    <div className={styles.PackagingMaterialOverview}>
      <PageHeader
        title="Packaging materials"
        description="Your materials database built from facility uploaded logistics packaging materials and product packaging materials. Manage material names and assign an impact reference to each material."
        right={
          <>
            {/*<HelpButton*/}
            {/*  type="text"*/}
            {/*  url="#"*/}
            {/*/>*/}
            <MoreButton
              actions={
                <ActionsPopOverItem
                  to="/impact_references"
                  icon={<SettingOutlined />}
                >
                  Manage impact references
                </ActionsPopOverItem>
              }
            />
          </>
        }
      />
      <div>
        <TableHeader
          style={{ height: 64 }}
          left={
            <>
              {selectedMaterials.length > 0 ? (
                <span className={styles["num-selected"]}>{selectedMaterials.length} selected:</span>
              ) : (
                <YearSelector
                  disableYears={showUnrelated}
                  optionData={materialsYears}
                  onChange={resetTable}
                  right={
                    <Tooltip
                      placement="bottom"
                      title="Show materials that are not related to purchase or sales orders"
                    >
                      <>
                        <SwitchWithLabel
                          togglePosition="right"
                          size="small"
                          checked={showUnrelated}
                          onChange={(disabled) => {
                            resetTable();
                            setShowUnrelated(disabled);
                          }}
                        >
                          Year unrelated
                        </SwitchWithLabel>
                      </>
                    </Tooltip>
                  }
                />
              )}
              <PageButtonGroup loading={isLoading}>
                {selectedMaterials.length > 0 ? (
                  <>
                    <MaterialImpactModal materials={selectedMaterials}>
                      <Button icon={<EditOutlined />}>Add/update impact reference</Button>
                    </MaterialImpactModal>
                    <DeletePackagingMaterialAction
                      deselectUndeletableProducts={deselectUndeletableMaterials}
                      materials={selectedMaterials}
                      onSucces={() => {
                        setSelectedMaterials([]);
                        analytics.track("Packaging Materials Deleted");
                      }}
                    >
                      <Button
                        icon={<DeleteOutlined style={{ color: "#f5222d" }} />}
                        disabled={selectedMaterials.some(
                          (x) => x.packaging_relations > 0 || x.facility_relations > 0
                        )}
                      />
                    </DeletePackagingMaterialAction>
                    <Dropdown
                      trigger={["click"]}
                      onOpenChange={handleOpenChange}
                      open={moreDropdownOpen}
                      menu={{
                        items: [
                          {
                            label: (
                              <DeleteMaterialImpactReferenceAction
                                materialIds={selectedMaterials.map((item) => item.id)}
                                onSuccess={() => setMoreDropdownOpen(false)}
                              >
                                <Space>
                                  <DeleteOutlined />
                                  <span>Delete impact reference</span>
                                </Space>
                              </DeleteMaterialImpactReferenceAction>
                            ),
                            key: "1",
                            danger: true,
                            disabled: selectedMaterials.length === 0,
                          },
                        ],
                      }}
                    >
                      <Button icon={<MoreOutlined />} />
                    </Dropdown>
                  </>
                ) : (
                  <></>
                )}
              </PageButtonGroup>
              {selectedMaterials.length > 0 && (
                <FakeLink onClick={() => setSelectedMaterials([])}>Clear selection</FakeLink>
              )}
            </>
          }
          right={
            <StatusTags
              data={data}
              loading={isLoading}
            />
          }
        />
        <Table<PackagingMaterial>
          availableFilters={{
            status: [
              { label: "Complete", value: "complete" },
              { label: "Incomplete", value: "incomplete" },
              { label: "Out of scope", value: "out_of_scope" },
            ],
            relation: [
              { label: "Source", value: "primary_source" },
              { label: "Proxy", value: "proxy" },
            ],
          }}
          loading={isLoading || isFetching}
          rowKey="id"
          checkable={true}
          data={data?.data}
          onSearchFilterSortPaginate={onSearchFilterSortPaginate}
          onSelectedKeysChange={onSelectedKeysChange}
          selectedKeys={selectedMaterials.map((x) => x.id)}
          totalRows={data?.total_count}
          pagination={pagination}
          sort={sort}
          search={search}
          filter={filter}
          columns={columns}
          rowLink={(record) => `${record.id}`}
        />
      </div>
    </div>
  );
}
